import ReportHeader from "../../event/components/report/ReportHeader";
import ReportSectionTitle from "../../../components/common/ReportSectionTitle";
import localize from "../../../common/localize";
import { getValueInCurrency } from "../../../common/utils";
import {
  getCurrencyFromSetup,
  getSettlementStatusName,
} from "../../../common/EntitiesManagement";
import {
  getSettledTotal,
  isSettled,
  SettlementReceiptLine,
} from "./settlementReceipt";
import {
  filterSettlementDetails,
  EMPTY_SETTLEMENT_FILTERS,
} from "./settlementFilterUtils";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : localize.no_data;

// Renders a single settlement's report block (header + observations + grouped
// detail). Only settled details (Approved/Payed) are shown; optional `filters`
// further restrict the details. No print button or page wrapper is included so
// this can be reused standalone or stacked in the "all settlements" report.
export default function SettlementReportBody({
  settlement,
  details,
  filters = EMPTY_SETTLEMENT_FILTERS,
}) {
  const currency = getCurrencyFromSetup();
  const code = (settlement?.Uuid ?? "").split("-")[0];
  const status =
    settlement?.status ?? settlement?.SettlementStatus ?? settlement?.Status;
  const filteredDetails = filterSettlementDetails(details, filters).filter(
    isSettled,
  );
  const grandTotal = getSettledTotal(filteredDetails);

  // Group by Service Order (event) then Personal.
  const eventsMap = new Map();
  (filteredDetails ?? []).forEach((item) => {
    const eventKey = item?.event?.Id ?? "unknown";
    if (!eventsMap.has(eventKey)) {
      eventsMap.set(eventKey, {
        code: item?.event?.Code ?? localize.no_data,
        personalsMap: new Map(),
        items: [],
      });
    }
    const group = eventsMap.get(eventKey);
    group.items.push(item);
    const personalKey =
      item?.personal?.id ?? item?.personal?.FullName ?? "unknown";
    if (!group.personalsMap.has(personalKey)) {
      group.personalsMap.set(personalKey, {
        name: item?.personal?.FullName ?? localize.no_data,
        items: [],
      });
    }
    group.personalsMap.get(personalKey).items.push(item);
  });
  const eventGroups = Array.from(eventsMap.values()).map((g) => ({
    ...g,
    personals: Array.from(g.personalsMap.values()),
  }));

  return (
    <>
      <ReportHeader
        event={{}}
        customData={[
          localize.settlement_detail_title,
          `${localize.code}: ${code}`,
          `${localize.modification_date}: ${formatDate(
            settlement?.ModificationDate,
          )}`,
          `${localize.created_by}: ${settlement?.CreatedBy ?? localize.no_data}`,
          `${localize.status}: ${getSettlementStatusName(status)}`,
        ]}
      />

      {settlement?.Observations && (
        <>
          <ReportSectionTitle title={localize.observations} />
          <div className="report_section_without_top_border">
            <div style={{ padding: "8px 12px" }}>
              <span
                className="report_little_text"
                style={{ whiteSpace: "pre-line" }}
              >
                {settlement.Observations}
              </span>
            </div>
          </div>
        </>
      )}

      <ReportSectionTitle title={localize.settlement_detail_title} />
      <div className="report_section_without_top_border">
        {filteredDetails?.length > 0 ? (
          <div style={{ padding: "8px 12px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: "bold",
                borderBottom: "1px solid #000",
                paddingBottom: "4px",
                marginBottom: "8px",
              }}
            >
              <span className="report_little_text">
                {localize.settlement_total_settled_value}
              </span>
              <span
                className="report_little_text"
                style={{ textAlign: "right" }}
              >
                {getValueInCurrency(currency, grandTotal)}
              </span>
            </div>

            {eventGroups.map((eventGroup, eIndex) => {
              const eventTotal = getSettledTotal(eventGroup.items);
              return (
                <div
                  key={eIndex}
                  className="print-row"
                  style={{ marginBottom: "14px" }}
                >
                  <div
                    className="report_little_text"
                    style={{
                      fontWeight: "bold",
                      borderBottom: "1px solid #000",
                      paddingBottom: "2px",
                    }}
                  >
                    {`${localize.service_order}: ${eventGroup.code}`}
                  </div>

                  {eventGroup.personals.map((personal, pIndex) => {
                    const personalTotal = getSettledTotal(personal.items);
                    return (
                      <div
                        key={pIndex}
                        style={{
                          marginTop: "6px",
                          marginBottom: "6px",
                          paddingLeft: "12px",
                        }}
                      >
                        <div
                          className="report_little_text"
                          style={{ fontWeight: "bold" }}
                        >
                          {personal.name}
                        </div>
                        {personal.items.map((item, iIndex) => (
                          <SettlementReceiptLine
                            key={iIndex}
                            item={item}
                            index={iIndex}
                            showObservations
                          />
                        ))}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderTop: "1px dashed #999",
                            marginTop: "5px",
                            paddingLeft: "12px",
                          }}
                        >
                          <span
                            className="report_little_text"
                            style={{ fontWeight: "bold" }}
                          >
                            {`${localize.settlement_total_settled_value} (${personal.name})`}
                          </span>
                          <span
                            className="report_little_text"
                            style={{
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                              textAlign: "right",
                            }}
                          >
                            {getValueInCurrency(currency, personalTotal)}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderTop: "1px solid #000",
                      marginTop: "5px",
                      paddingTop: "2px",
                    }}
                  >
                    <span
                      className="report_little_text"
                      style={{ fontWeight: "bold" }}
                    >
                      {`${localize.settlement_total_settled_value} (${localize.service_order}: ${eventGroup.code})`}
                    </span>
                    <span
                      className="report_little_text"
                      style={{
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        textAlign: "right",
                      }}
                    >
                      {getValueInCurrency(currency, eventTotal)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <label className="report_little_text">
            {localize.settlement_report_no_settled_details}
          </label>
        )}
      </div>
    </>
  );
}
